import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

import type { components, paths } from '#/types/capi-generated';

import ApiBase from './ApiBase';
import type { ApiConfig, ApiMeta, GetTokenFunction } from './types';

export function retrieveErrorMessages(errors: any): string[] {
  return errors.response.data.errors.map((e2) => e2.detail);
}

export class CommercialApi extends ApiBase {
  getRefreshTokenFunc: GetTokenFunction = () => null;
  getAccessTokenFunc: GetTokenFunction = () => null;
  refreshTokenFunc?: () => any;
  logoutCallback?: () => void;

  loading = {};

  constructor(
    config: ApiConfig,
    meta: ApiMeta,
    getAccessTokenFunc: GetTokenFunction,
    getRefreshTokenFunc: GetTokenFunction,
    opts?: {
      logoutCallback?: () => void;
      additionalScopes?: string[];
      logPingEvents?: boolean;
      refreshTokenFunc?: () => void;
    },
  ) {
    super(config, meta);

    this.getAccessTokenFunc = getAccessTokenFunc;
    this.getRefreshTokenFunc = getRefreshTokenFunc;
    this.refreshTokenFunc = opts?.refreshTokenFunc;
    this.logoutCallback = opts?.logoutCallback;
  }

  requestInterceptor(
    config: InternalAxiosRequestConfig,
  ): InternalAxiosRequestConfig {
    const accessToken = this.getAccessTokenFunc();
    if (!config.headers) {
      return config;
    }
    if (config.headers.Authorization === false) {
      delete config.headers.Authorization;
      return config;
    }
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  }

  responseErrorHandler(error: AxiosError) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    const isAuthRequest = error.config.url?.includes('oauth/token');
    const isRetry = error.config.headers?.retry_attempt;
    if (error.response?.status === 401 && !isAuthRequest && !isRetry) {
      console.log('Retrying');
      return this.refreshTokenFunc()
        .then(() => {
          if (error.config.headers) {
            error.config.headers.retry_attempt = 1;
          }
          return this.$http.request(error.config);
        })
        .catch((err) => {
          console.log('Refresh token failed. Logging out');
          this.logoutCallback?.();
          throw err;
        });
    }

    if (error.response?.status === 401 && isRetry) {
      console.warn('auth error on retry - logging out');
      this.logoutCallback?.();
    }
    return Promise.reject(error);
  }

  async refreshToken() {
    const refreshToken = await this.getRefreshTokenFunc();
    if (!refreshToken) {
      console.warn('No refresh token found. Skipping refresh token.');
      return;
    }

    const response = await this.$http.post(
      'oauth/token/',
      {
        refreshToken,
        grantType: 'refresh_token',
      },
      {
        withCredentials: true,
        headers: {
          Authorization: false,
        },
      },
    );
    return response;
  }

  async loginWithUsernameAndPassword(
    username: string,
    password: string,
    clientId: string,
  ) {
    const path = `/oauth/token/` as keyof paths;
    type operation = paths['/oauth/token/']['post'];
    type successResponse =
      operation['responses'][201]['content']['application/json'];

    const response = await this.$http.post<successResponse>(
      path,
      {
        grantType: 'password',
        clientId,
        username,
        password,
        scopes: 'read:prices,read:access',
      },
      {
        headers: {
          'content-type': 'application/json',
          Authorization: false,
        },
      },
    );
    const result = response.data;
    return result;
  }

  async getAccount() {
    const path = `/v1.0/me/` as keyof paths;
    type operation = paths['/v1.0/me/']['get'];
    type successResponse =
      operation['responses'][200]['content']['application/json'];

    const response = await this.$http.get<successResponse>(path, {
      headers: {
        'content-type': 'application/json',
      },
    });
    const result = response.data;
    return result;
  }

  async fetchPublicPrices(contractId: string) {
    const path = `/6a7bfff7/${contractId}/`;
    type operation =
      paths['/v1.0/contracts/{contract_ticker_symbol}/price-releases/latest/']['get'];
    type successResponse =
      operation['responses'][200]['content']['application/json'];

    const response = await this.$http.get<successResponse>(path, {
      headers: {
        'content-type': 'application/json',
      },
    });
    const result = response.data.data;
    return result;
  }

  async fetchContracts() {
    const path = `/v1.0/contracts/` as keyof paths;
    type operation = paths['/v1.0/contracts/']['get'];
    type successResponse =
      operation['responses'][200]['content']['application/json'];

    const response = await this.$http.get<successResponse>(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
    });
    const result = response.data.data;
    return result;
  }

  async fetchPriceReleases(
    contractTickerSymbol: string,
    vesselType?: string,
    limit = 250,
    offset = 0,
  ) {
    const path =
      `/v1.0/contracts/${contractTickerSymbol}/price-releases/` as keyof paths;
    type operation =
      paths[`/v1.0/contracts/{contract_ticker_symbol}/price-releases/`]['get'];
    type successResponse =
      operation['responses'][200]['content']['application/json'];

    const response = await this.$http.get<successResponse>(path, {
      params: { limit, offset, 'vessel-type': vesselType },
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
    });
    const result = response.data.data;
    return result;
  }

  async fetchPriceReleaseByReleaseDate(
    contractTickerSymbol: string,
    releaseDate: string,
    vesselType?: string,
  ) {
    const path =
      `/v1.0/contracts/${contractTickerSymbol}/price-releases/` as keyof paths;
    type operation =
      paths[`/v1.0/contracts/{contract_ticker_symbol}/price-releases/`]['get'];
    type successResponse =
      operation['responses'][200]['content']['application/json'];

    const response = await this.$http.get<successResponse>(path, {
      params: { 'release-date': releaseDate, 'vessel-type': vesselType },
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
    });
    const result = response.data.data;
    return result;
  }

  async fetchLatestPriceRelease(
    contractTickerSymbol: string,
    vesselType?: string,
  ) {
    const path = `/v1.0/contracts/${contractTickerSymbol}/price-releases/latest/`;
    type operation =
      paths[`/v1.0/contracts/{contract_ticker_symbol}/price-releases/latest/`]['get'];
    type successResponse =
      operation['responses'][200]['content']['application/json'];

    const response = await this.$http.get<successResponse>(path, {
      params: {
        'vessel-type': vesselType,
      },
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
    });
    const result = response.data.data;
    return result;
  }

  async fetchLatestSparkRCsv(
    perVesselSize: components['schemas']['RegasCostsVesselSizeEnum'],
    unit: components['schemas']['RegasCostsUnitEnum'],
  ) {
    const path = `/v1.0/lng/access/regas-costs/latest/` as keyof paths;

    const response = await this.$http.get<string>(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'text/csv',
      },
      params: {
        'vessel-size': perVesselSize,
        unit,
      },
    });
    const result = response.data;
    return result;
  }

  async fetchSparkRListCsv(
    perVesselSize: components['schemas']['RegasCostsVesselSizeEnum'],
    unit: components['schemas']['RegasCostsUnitEnum'],
    start: string,
    end: string,
    limit = 250,
    offset = 0,
  ) {
    const path = `/v1.0/lng/access/regas-costs/` as keyof paths;

    const response = await this.$http.get<string>(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'text/csv',
      },
      params: {
        'vessel-size': perVesselSize,
        unit,
        start,
        end,
        offset,
        limit,
      },
    });
    const result = response.data;
    return result;
  }

  async fetchDesHubNetbacksListCsv(
    unit: components['schemas']['DesHubNetbacksUnit'],
    start: string,
    end: string,
  ) {
    const path = `/v1.0/lng/access/des-hub-netbacks/` as keyof paths;

    const response = await this.$http.get<string>(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'text/csv',
      },
      params: {
        unit,
        start,
        end,
      },
    });
    const result = response.data;
    return result;
  }

  async fetchLatestDesHubNetbacksCsv(
    unit: components['schemas']['DesHubNetbacksUnit'],
    terminalUuid?: string | null,
  ) {
    const path = `/v1.0/lng/access/des-hub-netbacks/latest/` as keyof paths;

    const response = await this.$http.get<string>(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'text/csv',
      },
      params: {
        unit,
        terminal_uuid: terminalUuid ?? undefined,
      },
    });
    const result = response.data;
    return result;
  }

  async fetchGasContracts(
    ticker: components['schemas']['GasContractsViewTickerEnum'],
    unit: components['schemas']['CAPIGasContractsUnitEnum'],
    start?: string,
    end?: string,
    limit?: number,
    offset?: number,
  ) {
    const path = `/v1.0/gas/contracts/${ticker}/`;

    const response = await this.$http.get<string>(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'text/csv',
      },
      params: {
        unit,
        start,
        end,
        limit,
        offset,
      },
    });
    const result = response.data;
    return result;
  }

  async fetchGasTransitReferenceData() {
    const path = '/v1.0/gas/transit/reference-data/' as keyof paths;
    const response = await this.$http.get<
      components['schemas']['ListResponseModel_CAPIGasTransitRoutePairDTOAttr_']
    >(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
    });
    return response.data;
  }

  async fetchGasTransitCosts(
    unit: components['schemas']['CAPIGasTransitCostsUnitEnum'],
    opts: {
      origin?: string;
      destination?: string;
      type?: components['schemas']['CAPIGasTransitTypeEnum'];
      product?: components['schemas']['CAPIGasTransitProductEnum'];
      start?: string;
      end?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) {
    const path = '/v1.0/gas/transit/costs/' as keyof paths;

    const response = await this.$http.get<string>(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'text/csv',
      },
      params: {
        unit,
        ...opts,
      },
    });
    return response.data;
  }

  async fetchIntradayArbsReferenceData() {
    const path = '/v1.0/intraday/arbs/reference-data/' as keyof paths;
    const response = await this.$http.get<
      components['schemas']['commercial_api__views__intraday__v1_0__ref_data__IntradayRefDataResponse']
    >(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
    });
    return response.data;
  }

  async fetchIntradayBreakevensReferenceData() {
    const path = '/v1.0/intraday/breakevens/reference-data/' as keyof paths;
    const response = await this.$http.get<
      components['schemas']['commercial_api__views__intraday__v1_0__ref_data__IntradayRefDataResponse']
    >(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
    });
    return response.data;
  }

  async fetchIntradayContractsLive(
    contract: components['schemas']['IntradayContract'],
    unit: components['schemas']['commercial_api__views__intraday__v1_0__contracts__common__IntradayContractUnit'],
  ) {
    const path = '/v1.0/intraday/contracts/live/' as keyof paths;
    const response = await this.$http.get<
      components['schemas']['IntradayContractsLiveResponseModel']
    >(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
      params: { contract, unit },
    });
    return response.data;
  }

  async fetchIntradayContractsHistorical(
    contract: components['schemas']['IntradayContract'],
    unit: components['schemas']['commercial_api__views__intraday__v1_0__contracts__common__IntradayContractUnit'],
    start: string,
    end: string,
  ) {
    const path = '/v1.0/intraday/contracts/historical/' as keyof paths;
    const response = await this.$http.get<
      components['schemas']['IntradayHistoricalResponse']
    >(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
      params: { contract, unit, start, end },
    });
    return response.data;
  }

  async fetchIntradayArbsLive(
    unit: components['schemas']['IntradayUnit'],
    fobPort: string,
    viaPoint?: components['schemas']['PointEnum'] | null,
    opts?: {
      percentHire?: components['schemas']['IntradayPercentageEnum'];
      extendedCurve?: boolean;
    },
  ) {
    const path = '/v1.0/intraday/arbs/live/' as keyof paths;
    const response = await this.$http.get<
      components['schemas']['IntradayArbLiveResponseModel']
    >(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
      params: {
        unit,
        'fob-port': fobPort,
        'via-point': viaPoint ?? undefined,
        'percent-hire': opts?.percentHire,
        'extended-curve': opts?.extendedCurve,
      },
    });
    return response.data;
  }

  async fetchIntradayArbsHistorical(
    unit: components['schemas']['IntradayUnit'],
    fobPort: string,
    start: string,
    end: string,
    viaPoint?: components['schemas']['PointEnum'] | null,
    opts?: {
      percentHire?: components['schemas']['IntradayPercentageEnum'];
      extendedCurve?: boolean;
    },
  ) {
    const path = '/v1.0/intraday/arbs/historical/' as keyof paths;
    const response = await this.$http.get<
      components['schemas']['IntradayHistoricalResponse']
    >(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
      params: {
        unit,
        'fob-port': fobPort,
        'via-point': viaPoint ?? undefined,
        'percent-hire': opts?.percentHire,
        'extended-curve': opts?.extendedCurve,
        start,
        end,
      },
    });
    return response.data;
  }

  async fetchIntradayBreakevensLive(
    unit: components['schemas']['IntradayUnit'],
    breakevenType: components['schemas']['CAPIIntradayBreakevenType'],
    fobPort: string,
    viaPoint?: components['schemas']['PointEnum'] | null,
    opts?: {
      percentHire?: components['schemas']['IntradayPercentageEnum'];
      extendedCurve?: boolean;
    },
  ) {
    const path = '/v1.0/intraday/breakevens/live/' as keyof paths;
    const response = await this.$http.get<
      components['schemas']['commercial_api__views__intraday__v1_0__breakevens__live__IntradayBreakevenLiveResponse']
    >(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
      params: {
        unit,
        'breakeven-type': breakevenType,
        'fob-port': fobPort,
        'via-point': viaPoint ?? undefined,
        'percent-hire': opts?.percentHire,
        'extended-curve': opts?.extendedCurve,
      },
    });
    return response.data;
  }

  async fetchIntradayBreakevensHistorical(
    unit: components['schemas']['IntradayUnit'],
    breakevenType: components['schemas']['CAPIIntradayBreakevenType'],
    fobPort: string,
    start: string,
    end: string,
    viaPoint?: components['schemas']['PointEnum'] | null,
    opts?: {
      percentHire?: components['schemas']['IntradayPercentageEnum'];
      extendedCurve?: boolean;
    },
  ) {
    const path = '/v1.0/intraday/breakevens/historical/' as keyof paths;
    const response = await this.$http.get<
      components['schemas']['IntradayHistoricalResponse']
    >(path, {
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
      },
      params: {
        unit,
        'breakeven-type': breakevenType,
        'fob-port': fobPort,
        'via-point': viaPoint ?? undefined,
        'percent-hire': opts?.percentHire,
        'extended-curve': opts?.extendedCurve,
        start,
        end,
      },
    });
    return response.data;
  }
}
